import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity, Bell, BellRing, Building2, Users, Star, MapPin, ArrowUpRight, ArrowDownRight,
  Minus, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Label, EmptyState, scoreColor } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { fmtDateTime } from "../../../lib/time";
import { playPing } from "../../../components/useLiveRatings";

const PERIODS = [
  { v: "day", label: "Day" },
  { v: "week", label: "Week" },
  { v: "month", label: "Month" },
];

const REFRESH_MS = 60_000;
const BELL_POLL_MS = 20_000; // ratings bell polls faster than the dashboard

export default function PlatformLiveDashboard() {
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState(null);
  const [drillOrgId, setDrillOrgId] = useState(null);
  const [bell, setBell] = useState({ unread: 0, items: [] });
  const [bellOpen, setBellOpen] = useState(false);
  const lastSeen = useRef(null);
  const lastRefresh = useRef(null);

  const load = useCallback(async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const { data } = await api.get(`/platform/live-dashboard?period=${period}&tz=${encodeURIComponent(tz)}`);
      setData(data);
      lastRefresh.current = new Date();
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);

  // Initial + period change
  useEffect(() => { load(); }, [load]);

  // 60-sec dashboard auto-refresh
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Rating bell poll — delta-only via `since`; chimes on new items when the SoundToggle isn't muted.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const sinceParam = lastSeen.current ? `?since=${encodeURIComponent(lastSeen.current)}` : "";
        const { data } = await api.get(`/platform/activity-feed${sinceParam}`);
        if (cancelled) return;
        const isFirstPoll = !lastSeen.current;
        if (data.items?.length) {
          if (!isFirstPoll && localStorage.getItem("sv_ping_muted") !== "1") {
            playPing(data.items.some((x) => (x.rating ?? 5) <= 2));
          }
          setBell((b) => ({
            unread: isFirstPoll ? 0 : (b.unread || 0) + data.items.length,
            items: isFirstPoll ? data.items : [...data.items, ...b.items].slice(0, 50),
          }));
        }
        lastSeen.current = data.generated_at;
      } catch { /* keep polling */ }
    };
    poll();
    const t = setInterval(poll, BELL_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return <div className="text-slate-400">Loading live dashboard…</div>;
  const { totals, organizations: rows } = data;

  const openBell = () => { setBellOpen(true); setBell((b) => ({ ...b, unread: 0 })); };

  return (
    <div className="space-y-6" data-testid="platform-live-dashboard">
      {/* Header + bell + period toggle */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Servantage · Live</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Live Dashboard</h1>
          <p className="mt-1 text-xs text-slate-400">
            Auto-refreshes every 60s · last {lastRefresh.current ? fmtDateTime(lastRefresh.current.toISOString()) : "—"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={openBell} data-testid="rating-bell"
            className="relative inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-indigo-300 hover:text-indigo-700">
            {bell.unread > 0 ? <BellRing className="h-4 w-4 text-indigo-600" /> : <Bell className="h-4 w-4 text-slate-500" />}
            New ratings
            {bell.unread > 0 && (
              <span className="ml-1 rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white" data-testid="bell-count">
                {bell.unread}
              </span>
            )}
          </button>
          <div className="flex rounded-lg border border-slate-200 bg-white p-1" data-testid="period-switch">
            {PERIODS.map((p) => (
              <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`period-${p.v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                  period === p.v ? "bg-[#4338CA] text-white" : "text-slate-500 hover:bg-slate-50"
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Platform-wide KPI strip */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Active organisations" value={`${totals.active_organizations} / ${totals.organizations}`}
             icon={Building2} testId="kpi-orgs" />
        <KPI label="Active employees" value={`${totals.employees_active.toLocaleString()} / ${totals.employees_total.toLocaleString()}`}
             icon={Users} testId="kpi-employees" />
        <KPI label="Ratings this period" value={totals.ratings.toLocaleString()}
             delta={totals.delta_pct} icon={Star} testId="kpi-ratings" accent="#4338CA" />
        <KPI label={`Previous ${period}`} value={totals.ratings_prev.toLocaleString()}
             icon={Activity} testId="kpi-prev" />
      </div>

      {/* Per-org activity grid */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="font-head text-sm font-bold text-slate-900">Activity by organisation</span>
          <span className="text-xs text-slate-400">Sorted by ratings this {period} · click a row to drill in</span>
        </div>
        {rows.length === 0 ? (
          <EmptyState icon={Building2} title="No organisations" />
        ) : (
          <div className="overflow-x-auto sv-scroll">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-2">Organisation</th>
                  <th className="px-4 py-2">Area</th>
                  <th className="px-4 py-2">Employees</th>
                  <th className="px-4 py-2">Ratings</th>
                  <th className="px-4 py-2">Δ vs prev</th>
                  <th className="px-4 py-2">Last activity</th>
                  <th className="px-4 py-2">Contact</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} onClick={() => setDrillOrgId(o.id)}
                      data-testid={`live-row-${o.id}`}
                      className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-indigo-50/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-300" />
                        <div>
                          <p className="font-semibold text-slate-800">{o.name}</p>
                          <p className="text-xs text-slate-400">{o.industry || "—"} · {o.locations} loc</p>
                        </div>
                        {o.status === "suspended" && (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600">suspended</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{o.area || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <span className="font-semibold text-slate-800">{o.employees_active}</span>
                      <span className="text-slate-400"> / {o.employees_total}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="sv-metric font-bold text-slate-900">{o.ratings}</span>
                    </td>
                    <td className="px-4 py-3"><Delta pct={o.delta_pct} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {o.last_active ? fmtDateTime(o.last_active) : <span className="text-slate-300">never</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <ContactLinks c={o.contact} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drill-through sheet */}
      <OrgDrillSheet orgId={drillOrgId} onClose={() => setDrillOrgId(null)} />

      {/* Bell sheet */}
      <Sheet open={bellOpen} onOpenChange={setBellOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sv-scroll sm:max-w-md">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><Bell className="h-4 w-4" /> New ratings</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2" data-testid="bell-feed">
            {bell.items.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">Nothing new yet. We'll chime the next rating.</p>
            ) : bell.items.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 p-3" data-testid={`bell-item-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{r.org_name}</span>
                  <span className="sv-metric font-bold" style={{ color: scoreColor(r.rating || 3) }}>{Number(r.rating ?? 0).toFixed(1)}★</span>
                </div>
                {r.employee_name && (
                  <p className="mt-1 text-sm font-semibold text-indigo-700" data-testid={`bell-emp-${r.id}`}>{r.employee_name}</p>
                )}
                <p className="mt-0.5 text-xs text-slate-500">
                  {r.location_name || "—"}
                </p>
                {r.comment && <p className="mt-1 line-clamp-2 text-xs text-slate-600">{r.comment}</p>}
                <p className="mt-1 text-xs text-slate-400">{fmtDateTime(r.created_at)}</p>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function KPI({ label, value, delta, icon: Icon, testId, accent = "#0F172A" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
        <Icon className="h-4 w-4 text-slate-300" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="sv-metric text-2xl font-bold" style={{ color: accent }}>{value}</span>
        {delta !== undefined && delta !== null && <Delta pct={delta} />}
      </div>
    </div>
  );
}

function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-300">—</span>;
  if (pct === 0) return <span className="inline-flex items-center gap-0.5 text-xs text-slate-500"><Minus className="h-3 w-3" />0%</span>;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-bold ${
      up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
    }`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(pct)}%
    </span>
  );
}

function ContactLinks({ c, compact }) {
  if (!c || (!c.email && !c.phone)) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {!compact && c.name && <span className="font-semibold text-slate-700">{c.name}</span>}
      {c.email && (
        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}
           className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid={`contact-email-${c.email}`}>
          <Mail className="h-3 w-3" />{c.email}
        </a>
      )}
      {c.phone && (
        <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}
           className="inline-flex items-center gap-1 text-slate-600 hover:text-indigo-700" data-testid={`contact-phone-${c.phone}`}>
          <Phone className="h-3 w-3" />{c.phone}
        </a>
      )}
    </div>
  );
}

function OrgDrillSheet({ orgId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    if (!orgId) { setDetail(null); setFeed([]); return; }
    api.get(`/platform/organizations/${orgId}`).then((r) => setDetail(r.data)).catch(() => {});
    // Pull today's feed for this org via main feedback endpoint scoped to org via impersonation would be heavier;
    // instead reuse activity-feed and client-filter.
    api.get("/platform/activity-feed?limit=100").then((r) => setFeed((r.data.items || []).filter((x) => x.org_id === orgId))).catch(() => {});
  }, [orgId]);

  return (
    <Sheet open={!!orgId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sv-scroll sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {detail?.organization?.name || "Loading…"}
          </SheetTitle>
        </SheetHeader>
        {!detail ? (
          <p className="mt-6 text-sm text-slate-400">Loading organisation…</p>
        ) : (
          <div className="mt-4 space-y-5 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Mini label="Service Index" value={detail.metrics.ssi.toFixed(2)} color={scoreColor(detail.metrics.ssi)} />
              <Mini label="Interactions" value={detail.metrics.verified_interactions} />
              <Mini label="Open recovery" value={detail.metrics.open_recovery} />
            </div>

            {/* Locations with employees and today's samples */}
            <div>
              <Label>Locations</Label>
              <div className="mt-2 space-y-1">
                {(detail.locations || []).length === 0 ? (
                  <p className="rounded-lg bg-slate-50 p-3 text-slate-400">No locations yet.</p>
                ) : detail.locations.map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded-lg bg-slate-50 p-2" data-testid={`drill-loc-${l.id}`}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-slate-400" />
                      <span className="font-medium text-slate-700">{l.name}</span>
                      <span className="text-xs text-slate-400">· {l.city}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      SSI <b className="text-slate-700">{l.ssi.toFixed(1)}</b> · {l.employees} emp · {l.nfc_provisioned} NFC
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent ratings (today) scoped to this org */}
            <div>
              <Label>Latest ratings</Label>
              <div className="mt-2 space-y-1">
                {feed.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-400">No recent ratings in the live feed.</p>
                ) : feed.slice(0, 20).map((r) => (
                  <div key={r.id} className="rounded-lg border border-slate-100 p-2 text-xs"
                       data-testid={`drill-feed-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">
                        {Number(r.rating ?? 0).toFixed(1)}★
                        {r.employee_name && <span className="ml-2 text-indigo-700">{r.employee_name}</span>}
                      </span>
                      <span className="text-slate-400">{fmtDateTime(r.created_at)}</span>
                    </div>
                    <p className="text-slate-500">{r.location_name || "—"}</p>
                  </div>
                ))}
              </div>
            </div>

            {detail.contact && (
              <div className="rounded-lg border border-slate-200 p-3">
                <Label className="mb-1 block normal-case tracking-normal text-slate-600">Signup contact</Label>
                <ContactLinks c={detail.contact} />
                <p className="mt-2 text-xs text-slate-400">
                  Industry: {detail.organization?.industry || "—"} · Country: {detail.organization?.country || "—"} ·
                  Signup {fmtDateTime(detail.organization?.created_at)}
                </p>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Mini({ label, value, color = "#0F172A" }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="sv-metric text-xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}
